/**
 * LibraryPicker.js
 *
 * Host-only popup in the Auction lobby to choose what is being auctioned:
 * 1. A default library (built into the app, or marked default in the database)
 * 2. Any library by its ID
 * 3. A new library, uploaded from an Excel / Numbers / CSV file and saved to the database
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, FileSpreadsheet, Upload, X } from "lucide-react";
import toast from "react-hot-toast";
import Button from "../../../components/ui/Button";
import { useClientAuth } from "../../../hooks/clientAuth";
import { setRoomLibrary } from "../helpers/auctionLobby";
import { fetchDefaultLibraries, fetchLibrary, saveLibrary } from "../library/libraryStore";
import { ACCEPTED_FILE_TYPES, parseLibraryFile } from "../library/parseLibraryFile";

const PREVIEW_ITEMS = 5;

const kindLabels = {
    name        :   "Name",
    score       :   "Judged on",
    category    :   "Category",
    number      :   "Number",
    text        :   "Text",
};

const LibraryPickerModal = ({ room, onClose }) => {
    const { user } = useClientAuth();
    const [defaults, setDefaults] = useState(null);
    const [libraryID, setLibraryID] = useState("");
    const [upload, setUpload] = useState(null);         // Parsed file waiting to be saved
    const [uploadName, setUploadName] = useState("");
    const [uploadDescription, setUploadDescription] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetchDefaultLibraries().then(setDefaults);
    }, []);

    /**
     * Uses a library for this room
     * @param {Object} library  -   Full library (with items)
     */
    const applyLibrary = async (library) => {
        const saved = await setRoomLibrary(room.id, library);
        if (saved) {
            toast.success(`Playing ${library.name}`);
            onClose();
        } else {
            toast.error("Couldn't set the library");
        }
    };

    const chooseByID = async (code) => {
        if (!code.trim()) {
            toast.error("Enter a library ID first.");
            return;
        }
        setBusy(true);
        const library = await fetchLibrary(code);
        if (library) await applyLibrary(library);
        else toast.error("No library with that ID");
        setBusy(false);
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        setBusy(true);
        try {
            const parsed = await parseLibraryFile(file);
            setUpload(parsed);
            setUploadName(file.name.replace(/\.[^.]+$/, ""));
            setUploadDescription("");
        } catch (error) {
            toast.error(error.message, { duration: 6000 });
        }
        setBusy(false);
    };

    const saveUpload = async () => {
        if (!uploadName.trim()) {
            toast.error("Give your library a name.");
            return;
        }
        setBusy(true);
        const library = await saveLibrary(
            { ...upload, name: uploadName.trim(), description: uploadDescription.trim() || null },
            user
        );
        if (library) {
            toast.success(`Saved! Library ID: ${library.code.toUpperCase()}`, { duration: 8000 });
            await applyLibrary(library);
        } else {
            toast.error("Couldn't save the library");
        }
        setBusy(false);
    };

    const inputClasses = "w-full rounded-xl border border-zinc-700 bg-black p-3 text-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow";
    const sectionTitle = "mt-8 mb-3 text-sm uppercase tracking-widest text-zinc-500";

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        >
            <motion.div
                initial={{ scale: 0.95, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl border border-zinc-800 bg-zinc-950 p-6 md:p-8 text-white text-left"
            >
                <button onClick={onClose} aria-label="Close" className="absolute top-5 right-5 text-zinc-500 hover:text-white">
                    <X size={24} />
                </button>

                <h2 className="text-3xl font-extrabold">What are we auctioning?</h2>
                <p className="mt-1 text-zinc-400">Pick a library, enter a library ID, or upload your own list.</p>

                {upload ? (
                    /* --- Preview of an uploaded file --- */
                    <div>
                        <h3 className={sectionTitle}>Your library</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label>
                                <span className="block mb-1 text-zinc-300">Name</span>
                                <input value={uploadName} onChange={(e) => setUploadName(e.target.value)} className={inputClasses} maxLength={60} />
                            </label>
                            <label>
                                <span className="block mb-1 text-zinc-300">Description (optional)</span>
                                <input value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} className={inputClasses} maxLength={160} />
                            </label>
                        </div>

                        <h3 className={sectionTitle}>Attributes · {upload.itemCount} items</h3>
                        <div className="flex flex-wrap gap-2">
                            {upload.attributes.map((attribute) => (
                                <span
                                    key={attribute.name}
                                    className={`rounded-full border px-3 py-1 text-sm ${attribute.kind === "score" ? "border-yellow text-yellow" : "border-zinc-700 text-zinc-300"}`}
                                >
                                    {attribute.name} <span className="text-zinc-500">· {kindLabels[attribute.kind]}</span>
                                </span>
                            ))}
                        </div>
                        <p className="mt-2 text-sm text-zinc-500">
                            Category attributes (values that repeat) can have limits and bonuses in Lobby settings.
                        </p>

                        <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-800">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-900 text-zinc-400">
                                    <tr>
                                        {upload.attributes.map((attribute) => (
                                            <th key={attribute.name} className="px-3 py-2 text-left font-medium whitespace-nowrap">{attribute.name}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {upload.items.slice(0, PREVIEW_ITEMS).map((item, index) => (
                                        <tr key={index} className="border-t border-zinc-800">
                                            {upload.attributes.map((attribute) => (
                                                <td key={attribute.name} className="px-3 py-2 whitespace-nowrap">
                                                    {attribute.kind === "name" ? item.name
                                                        : attribute.kind === "score" ? item.score
                                                        : String(item.values[attribute.name] ?? "")}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {upload.itemCount > PREVIEW_ITEMS && (
                            <p className="mt-2 text-sm text-zinc-500">…and {upload.itemCount - PREVIEW_ITEMS} more</p>
                        )}

                        {upload.warnings.map((warning) => (
                            <p key={warning} className="mt-3 text-sm text-peach">{warning}</p>
                        ))}

                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button variant="main" size="lg" onClick={saveUpload} disabled={busy} className="flex-1 rounded-xl">
                                {busy ? "Saving..." : "Save & play this library"}
                            </Button>
                            <Button variant="outline" size="lg" onClick={() => setUpload(null)} disabled={busy} className="rounded-xl text-zinc-200 hover:bg-zinc-900">
                                Back
                            </Button>
                        </div>
                        <p className="mt-3 text-sm text-zinc-500">
                            Saving gives your library an ID that anyone can use to play it.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* --- Default libraries --- */}
                        <h3 className={sectionTitle}>Default libraries</h3>
                        {defaults === null ? (
                            <p className="text-zinc-500">Loading…</p>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {defaults.map((library) => {
                                    const isCurrent = room.library?.code === library.code;
                                    return (
                                        <button
                                            key={library.code}
                                            onClick={() => chooseByID(library.code)}
                                            disabled={busy}
                                            className={`rounded-2xl border p-5 text-left transition-colors hover:border-zinc-500 ${isCurrent ? "border-yellow" : "border-zinc-800"}`}
                                        >
                                            <div className="text-xl font-bold">{library.name}</div>
                                            {library.description && <div className="mt-1 text-sm text-zinc-400">{library.description}</div>}
                                            <div className="mt-3 text-sm text-zinc-500">
                                                {library.itemCount} items · ID <span className="font-mono">{library.code.toUpperCase()}</span>
                                                {isCurrent && <span className="text-yellow"> · Playing</span>}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* --- By ID --- */}
                        <h3 className={sectionTitle}>Have a library ID?</h3>
                        <form
                            onSubmit={(e) => { e.preventDefault(); chooseByID(libraryID); }}
                            className="flex gap-3"
                        >
                            <input
                                value={libraryID}
                                onChange={(e) => setLibraryID(e.target.value)}
                                placeholder="e.g. K7MQ2X"
                                autoCapitalize="characters"
                                autoComplete="off"
                                spellCheck={false}
                                className={`${inputClasses} font-mono uppercase tracking-widest placeholder:tracking-normal`}
                            />
                            <Button type="submit" variant="main" size="lg" disabled={busy} className="rounded-xl gap-2 shrink-0">
                                Use <ArrowRight size={18} />
                            </Button>
                        </form>

                        {/* --- Upload --- */}
                        <h3 className={sectionTitle}>Upload your own</h3>
                        <label className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-zinc-700 p-8 text-center transition-colors ${busy ? "opacity-50" : "cursor-pointer hover:border-zinc-500"}`}>
                            <Upload size={32} className="text-zinc-400" />
                            <span className="text-lg">Excel, Numbers or CSV file</span>
                            <input type="file" accept={ACCEPTED_FILE_TYPES} onChange={handleFile} disabled={busy} className="hidden" />
                        </label>

                        <div className="mt-4 rounded-2xl bg-zinc-900 p-5 text-sm text-zinc-400">
                            <div className="flex items-center gap-2 text-zinc-200 font-medium mb-2">
                                <FileSpreadsheet size={16} /> How to lay out the file
                            </div>
                            <p>Put the attribute names in the <span className="text-white">first column</span>, and one item per column after that:</p>
                            <div className="mt-3 overflow-x-auto">
                                <table className="text-left font-mono text-xs">
                                    <tbody>
                                        {[["Player", "Messi", "Ronaldo", "…"], ["Country", "Argentina", "Portugal", "…"], ["Points", "95", "94", "…"]].map((row) => (
                                            <tr key={row[0]}>
                                                {row.map((cell, index) => (
                                                    <td key={index} className={`border border-zinc-700 px-3 py-1 ${index === 0 ? "text-white" : ""}`}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p className="mt-3">
                                One attribute must be called <span className="text-white">Rating</span>, <span className="text-white">Points</span> or <span className="text-white">Marks</span>: teams are judged on it.
                                The first other attribute names each item. Attribute names across the first row work too.
                            </p>
                        </div>
                    </>
                )}
            </motion.div>
        </motion.div>
    );
};

export default LibraryPickerModal;
